/* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */
// @TODO: use a new method like EditorStateProxy.createEmpty
import { MediaTypes } from '~publish/legacy/constants'

import type { Document } from './Document'
import type { FormattedProfileData } from './types'
import type {
  ComposerProfile,
  Subprofile,
  UserTag,
  ValidationCode,
} from '../stores/types'

export interface MediaSource {
  name: string
  trigger: string
  id?: string
}

export type Image = {
  url: string
  /** The file name the image was uploaded under, absent on other media. */
  name?: string
  /** Size in bytes, known only for an image uploaded in this session. */
  size?: number
  width: number | null
  height: number | null
  altText?: string | null
  source?: MediaSource | null
  mediaType: string
  sourceLink?: string
  originalUrl?: string | null
  userTags?: UserTag[]
}

export type Gif = {
  url: string
  stillGifUrl: string
  mediaType: string
  width: number | null
  height: number | null
  frameCount?: number | null
}

export type Video = {
  uploadId: string | null
  name: string
  duration: number
  durationMs: number
  size: number
  width: number
  height: number
  frameRate?: number
  audioCodec?: string | null
  url: string
  originalUrl: string
  thumbnail: string
  availableThumbnails?: string[]
  thumbOffset?: number | string
  mediaType: string
  wasEdited?: boolean
}

export type Media = Image | Gif | Video | Document

export type Link = {
  url: string
  expandedUrl: string | null | undefined
  title: string | null
  description: string | null
  thumbnail: Image | null
  thumbnailHttps: string | null
  availableThumbnails: Image[] | string[] | null
  originalThumbnailUrl?: string
  wasEdited: boolean
  scrapingError?: string
}

export type SourceLink = {
  url: string
  availableImages: Image[]
}

export type Retweet = {
  text: string
  tweetId: string | number
  userId: string
  userName: string
  userDisplayName: string
  tweetUrl: string
  avatarUrl: string
  comment?: string
  media?: RetweetMedia[]
}

export type RetweetMedia = {
  id: string
  type: 'photo' | 'video' | 'animated_gif'
  url: string
  width: number
  height: number
  duration_ms?: number
  variants?: Array<{
    bitrate?: number
    content_type?: string
    url?: string
  }>
}

type NewVideoInput = Omit<Video, 'mediaType' | 'wasEdited' | 'uploadId'> & {
  uploadId?: Video['uploadId']
}
type NewRetweetInput = Omit<Retweet, 'media'> & {
  media?: RetweetMedia[]
}
type NewPreventsSavingInput = {
  message: string
  composerId?: string | null
  code?: ValidationCode
}

// Link Attachment factory
export const getNewLink = ({
  url,
  expandedUrl,
  title = null,
  description = null,
  thumbnail = null,
  thumbnailHttps = null,
  availableThumbnails = null,
  wasEdited = false,
  scrapingError,
}: {
  url: string
  expandedUrl?: string | null
  title?: string | null
  description?: string | null
  thumbnail?: Image | null
  thumbnailHttps?: string | null
  availableThumbnails?: Image[] | string[] | null
  wasEdited?: boolean
  scrapingError?: string
}): Link => ({
  url,
  expandedUrl,
  title,
  description,
  thumbnail,
  thumbnailHttps,
  availableThumbnails,
  wasEdited,
  scrapingError,
})

// SourceLink Attachment factory (source url and its available images)
export const getNewSourceLink = (url: string): SourceLink => ({
  url,
  availableImages: [],
})

// Available images factory
export const getNewAvailableImage = (
  image: Pick<Image, 'url' | 'width' | 'height'>,
  sourceLink: string,
): Image => ({
  url: image.url,
  mediaType: MediaTypes.IMAGE,
  sourceLink,
  width: image.width,
  height: image.height,
})

// Image factory
export const getNewImage = ({
  url,
  width = null,
  height = null,
  altText = null,
  source = null,
  originalUrl = null,
  name,
  size,
}: {
  url: string
  width?: number | null
  height?: number | null
  altText?: string | null
  source?: MediaSource | null
  originalUrl?: string | null
  name?: string
  size?: number
}): Image => ({
  url,
  mediaType: MediaTypes.IMAGE,
  width,
  height,
  altText,
  source,
  originalUrl,
  name,
  size,
})

// Gif factory
export const getNewGif = (
  gifUrl: string,
  gifStillUrl: string,
  width: number | null = null,
  height: number | null = null,
  frameCount: number | null = null,
): Gif => ({
  url: gifUrl,
  stillGifUrl: gifStillUrl,
  mediaType: MediaTypes.GIF,
  width,
  height,
  frameCount,
})

// Video factory
export const getNewVideo = ({
  audioCodec,
  uploadId = null,
  name,
  duration,
  durationMs,
  size,
  width,
  height,
  url,
  originalUrl,
  thumbnail,
  availableThumbnails,
  thumbOffset,
}: NewVideoInput): Video => ({
  ...(audioCodec !== undefined ? { audioCodec } : {}),
  uploadId,
  name,
  duration,
  durationMs,
  size,
  width,
  height,
  url,
  originalUrl,
  thumbnail,
  availableThumbnails,
  thumbOffset,
  mediaType: MediaTypes.VIDEO,
  wasEdited: false,
})

// Retweet factory
export const getNewRetweet = ({
  text,
  tweetId,
  userId,
  userName,
  userDisplayName,
  tweetUrl,
  avatarUrl,
  media = [],
}: NewRetweetInput): Retweet => ({
  text,
  tweetId,
  userId,
  userName,
  userDisplayName,
  tweetUrl,
  avatarUrl,
  media,
})

export const getNewProfile = (data: FormattedProfileData): ComposerProfile => ({
  id: data.id,
  apiVersion: data.apiVersion,
  service: {
    name: data.serviceName,
    username: data.serviceUsername,
    formattedUsername: data.serviceFormattedUsername,
  },
  images: data.images,
  serviceName: data.serviceName,
  serviceUsername: data.serviceUsername,
  serviceFormattedUsername: data.serviceFormattedUsername,
  timezone: data.timezone,
  shouldBeAutoSelected: data.shouldBeAutoSelected,
  isSelected: false,
  isDisabled: data.isDisabled,
  subprofiles: data.subprofiles, // Data structure in getNewSubprofile()
  selectedSubprofileId: null,
  serviceType: data.serviceType,
  serviceId: data.serviceId,
  subprofilesOrignatedFromAPI: null,
  instagramDirectEnabled:
    data.instagramDirectEnabled && data.serviceName === 'instagram',
  isTwitterPremium: data.isTwitterPremium,
  canAssociateLocation: data.canAssociateLocation,
  locationData: data.locationData,
  profileHasPostingSchedule: data.profileHasPostingSchedule,
  defaultToReminders: data.defaultToReminders,
  displayName: data.displayName,
  serverUrl: data.serverUrl,
  // Profile-specific app state
  appState: {
    isSubprofileCreationPending: false,
  },
  shorteningDomain: data.shorteningDomain,
  isFake: data.isFake,
  utmTrackingEnabled: data.utmTrackingEnabled,
})

export const getNewSubprofile = ({
  avatar,
  id,
  serviceId,
  name,
  isShared,
}: Subprofile): Subprofile => ({
  avatar,
  id,
  serviceId,
  name,
  isShared,
})

export const getNewPreventsSavingObj = ({
  message,
  composerId = null,
  code = undefined,
}: NewPreventsSavingInput): {
  message: string
  composerId: string | null
  code: ValidationCode | undefined
} => ({ message, composerId, code })

export const isMediaImage = (media: Media): media is Image => {
  return media.mediaType === MediaTypes.IMAGE
}

export const isMediaGif = (media: Media): media is Gif => {
  return media.mediaType === MediaTypes.GIF
}

export const isMediaVideo = (media: Media): media is Video => {
  return media.mediaType === MediaTypes.VIDEO
}
